import { DEMO_PROJECT } from './demo-project';
import type { GgxTourDefinition } from './tour-types';

export const HOWTO_10: GgxTourDefinition = {
    key: '[HOWTO-10] Approvals for Reports',
    route: '/home',
    project: DEMO_PROJECT,
    estimatedMinutes: 4,
    prerequisite: {
        target: '[test-id="home-page-card-Data & AI Assets"]',
        message: 'Open the home page, then start this tour.',
    },
    intro: {
        headline: 'Whichever compass you use, true north stays the same',
        subtitle:
            'Approvals work the same way for everything in GGX: pipelines, prompts, models, ' +
            'reports, all of it. Someone reviews, someone signs off, and the object locks. To ' +
            'see how it works, we will follow one report, Response Accuracy (Credit Card ' +
            'Queries), from the first sign-off to the audit trail. Whatever you learn here, ' +
            'you can use on any object.',
    },
    outro: {
        title: "That's a wrap!",
        subtitle:
            'You just followed one report through approval, and the same steps apply to every ' +
            'other object in GGX. Every pipeline, prompt, model and function has the same tab, ' +
            'the same lock and the same trail. Next up, Settings & Governance Configurations ' +
            'shows you where those workflows and reviewers are set up.',
        learned: [
            'The **Approvals tab** shows who signed off and when.',
            '**Approved** locks the object; changing it means a new version.',
            'The **same tab, lock and trail** exist on every object in GGX.',
        ],
    },
    chapters: [
        {
            name: 'Find the report',
            blurb: 'From the home page to one approved report',
            icon: 'map',
            steps: [2, 3, 4, 5],
        },
        {
            name: 'Read the lock',
            blurb: 'What Approved means and where the reviews live',
            icon: 'compass',
            steps: [6, 7],
        },
        {
            name: 'The audit trail',
            blurb: 'Who signed off, when, and how to change a locked report',
            icon: 'list',
            steps: [8, 9, 10],
        },
    ],
    steps: [
        {
            target: '[test-id="home-page-card-Data & AI Assets"] [test-id="launch-button"]',
            title: 'Reports live with your other AI assets',
            whatHappens:
                'the module opens on the Agents page, with Reports further down its sidebar.',
            icon: 'map',
            description:
                'Reports live here in <strong>Data & AI Assets</strong> with your pipelines, ' +
                'prompts and models. Any of them can be approved, and the steps are always the ' +
                'same. We are using a report today because a locked report is what makes ' +
                'scores comparable. Click <strong>Launch</strong>.',
            side: 'bottom',
            align: 'center',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Launch',
        },
        {
            target: '[test-id="sidenav-item-tab-Reports"]',
            scrollIntoView: true,
            title: 'Open the Reports page',
            whatHappens:
                'the Reports page opens, with every report grouped by the risk it measures.',
            icon: 'map',
            description:
                '<strong>Reports</strong> is the catalogue of reusable metrics for the project. ' +
                'Some of them are drafts, some are waiting for review and a few are approved. ' +
                'The approved ones are the ones you can trust in a comparison, and we are about ' +
                'to see why. Click <strong>Reports</strong>.',
            side: 'right',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Reports',
        },
        {
            target: '[test-id^="group-Accuracy Reports"]',
            targetText: 'Accuracy Reports',
            climbToRow: true,
            waitForPresent: '[test-id="report-object-filter"]',
            advanceWhenPresent: '[test-id^="grid-row-link-Response Accuracy"]',
            title: 'Reports are grouped by the risk they measure',
            whatHappens: 'the group expands to list every accuracy report.',
            icon: 'grid',
            description:
                'Each group is a kind of risk, with the number of reports beside it. Approval ' +
                'is decided report by report, so a group can hold approved reports and drafts ' +
                'side by side. Click <strong>Accuracy Reports</strong> to open the group.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Accuracy Reports',
        },
        {
            target: '[test-id^="grid-row-link-Response Accuracy"]',
            targetText: 'Response Accuracy (Credit Card Queries)',
            climbToRow: true,
            scrollIntoView: true,
            title: 'Pick the approved one',
            whatHappens: "the report's page opens on its Details tab.",
            icon: 'search',
            description:
                '<strong>Response Accuracy (Credit Card Queries)</strong> is marked ' +
                '<strong>Approved</strong> while most of its neighbours are still drafts. That ' +
                'badge is a promise: the report you run today is exactly the one the reviewers ' +
                'looked at, and it will stay that way. Click the row to open it.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Response Accuracy (Credit Card Queries)',
        },
        {
            target: '[test-id="name-bar"]',
            title: 'Approved and locked',
            whatHappens: 'next, the tab where the reviews are kept.',
            icon: 'compass',
            description:
                'The header tells you two things at a glance. <strong>Version 1</strong> is the ' +
                'version you are looking at, and the green <strong>Approved</strong> badge means ' +
                'it is locked: nobody can edit its description, parameters or code any more, so ' +
                'a score it produced last month means the same thing as one it produces today. ' +
                'The Details tab below still shows everything about the report; it is simply ' +
                'read-only now. Click Continue.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="tab-item-Approvals"]',
            scrollIntoView: true,
            title: 'Every review is kept in one place',
            whatHappens: 'the Approvals tab opens with the review table and the activity log.',
            icon: 'list',
            description:
                'Every object has this tab, and it always looks the same: who signed off, what ' +
                'changed, and a log of what happened. Learn it once, and you can read it ' +
                'anywhere. Click <strong>Approvals</strong>.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Approvals',
        },
        {
            target: '[test-id="approval-status-card"]',
            frame: true,
            scrollIntoView: true,
            title: 'Who had to sign off, and did they',
            whatHappens: 'next, the time-stamped record of how the approval unfolded.',
            icon: 'grid',
            description:
                'Each row is one responsibility in the approval workflow: here a ' +
                '<strong>Compliance Reviewer</strong> and an <strong>MRM Reviewer</strong>, each ' +
                'with the person who actually reviewed, their decision and the date. Both show ' +
                '<strong>Accepted</strong>, which is why the report is approved. The dropdown ' +
                'on the right picks the approval workflow, so your team decides who needs to ' +
                'sign off before something is locked. Click Continue.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="activity-log-card"]',
            frame: true,
            scrollIntoView: true,
            title: 'The audit trail, in order',
            whatHappens: 'next, the one way a locked report is allowed to change.',
            icon: 'clock',
            description:
                'The <strong>Activity Log</strong> is the same story told in time order: the ' +
                'approval request going out, each reviewer accepting, and the status changing ' +
                'to Approved once the workflow rule was satisfied, all with names and ' +
                'timestamps. It also records findings and the replies to them, so a question ' +
                'like "why did compliance accept this?" has an answer on the page. Click ' +
                'Continue.',
            side: 'top',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="report-action-buttons"] [test-id="object-clone-button"]',
            title: 'Locked does not mean frozen forever',
            whatHappens: 'the tour wraps up.',
            icon: 'compass',
            description:
                'Sometimes an approved report does need to improve, say a better way of judging ' +
                'factual correctness. You do not edit it. You <strong>Clone</strong> it, which ' +
                'creates Version 2 as a new draft that goes through its own review, while ' +
                'Version 1 stays locked and keeps working for everyone who depends on it. GGX ' +
                'also remembers which version scored each evaluation, so old results stay ' +
                'reproducible. No need to click it now. Click Continue to finish.',
            side: 'bottom',
            align: 'end',
            buttons: ['previous', 'next'],
        },
    ],
};
